import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ModuleSettingCard from "./ModuleSettingCard";
import { useProduct } from "@hooks";
import { Icon, ProductIcon, Button, NoContentContainer } from "@components";
import {
	ModuleSetting,
	NewModuleSetting,
	getRootProductDescription,
	RootProductId,
} from "@models";
import UserDetailsPageContext from "../../../pages/user/UserDetailsPageContext";
import ExistingModuleSettings from "./ExistingModuleSettings";
import {
	copyModuleSetting,
	createDefaultModuleSetting,
	splitModuleSettings,
} from "./util";
import BalanceAccountCard from "./balanceAccounts/BalanceAccountCard";
import BalanceAccountControlsDropdown from "./balanceAccounts/BalanceAccountControlsDropdown";
import { colors } from "@theme/theming";

type Props = {
	rootProductId: RootProductId;
	moduleSettings: ModuleSetting[];
	showActiveModuleSettings: boolean;
};

export default function ModuleSettingCategorySection(props: Props) {
	const { rootProductId, moduleSettings, showActiveModuleSettings } = props;
	const { name: productName } = getRootProductDescription(rootProductId);
	const product = useProduct(rootProductId);

	const [newModuleSetting, setNewModuleSetting] = useState<
		NewModuleSetting | undefined
	>();

	const newCardRef = useRef<HTMLDivElement>(null);
	const content: React.ReactNode[] = [];

	const handleCopyModuleSetting = (ms: ModuleSetting) => {
		setNewModuleSetting(copyModuleSetting(ms));
	};

	const { user, division } = useContext(UserDetailsPageContext);
	const balanceAccount = user.balanceAccounts.find(
		(ba) => ba.budgetCategory === rootProductId
	);

	if (newModuleSetting) {
		content.push(
			<div key="new" ref={newCardRef}>
				<StyledModuleSettingCard
					isInitiallyExpanded
					rootProductId={rootProductId}
					moduleSetting={newModuleSetting}
					discardModuleSetting={() => setNewModuleSetting(undefined)}
				/>
			</div>
		);
	}

	if (balanceAccount) {
		content.push(
			<BalanceAccountCard
				key="balanceAccount"
				balanceAccount={balanceAccount}
				moduleSettings={moduleSettings}
			/>
		);
	}

	if (moduleSettings.length > 0) {
		content.push(
			<ExistingModuleSettings
				key="existing"
				showActiveModuleSettings={showActiveModuleSettings}
				moduleSettings={moduleSettings}
				rootProductId={rootProductId}
				handleCopyModuleSetting={handleCopyModuleSetting}
			/>
		);
	}

	if (content.length === 0) {
		content.push(
			<NoContentContainer key="no-content">
				Der Benutzer hat kein {productName}-Modul
			</NoContentContainer>
		);
	}

	const handleAddModuleSetting = () => {
		const { activeModuleSettings } = splitModuleSettings(moduleSettings);
		const hasActiveModuleSetting = activeModuleSettings.length > 0;
		setNewModuleSetting(
			createDefaultModuleSetting(
				product,
				user,
				product.id === "congrats" ? false : hasActiveModuleSetting,
				division?.categories
			)
		);
	};

	useEffect(() => {
		// after creating the new MS, the card ref is not available right away, so we have to defer this until after the next render
		if (newModuleSetting && newCardRef.current?.scrollIntoView) {
			newCardRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [newModuleSetting, newCardRef]);

	return (
		<>
			<SectionHeader>
				<GridContainer
					style={{ justifyContent: "start", alignItems: "center" }}
				>
					<ProductIcon productId={rootProductId} />
					{productName}
				</GridContainer>
				<GridContainer style={{ justifyContent: "end" }}>
					<AddModuleSettingButton onClick={handleAddModuleSetting}>
						<Icon.Add /> Neues {productName}-Modul
					</AddModuleSettingButton>
					<BalanceAccountControlsDropdown
						productId={rootProductId}
						balanceAccount={balanceAccount}
					/>
				</GridContainer>
			</SectionHeader>
			<ModuleSettingsContainer>{content}</ModuleSettingsContainer>
		</>
	);
}

export const StyledModuleSettingCard = styled(ModuleSettingCard)`
	margin-bottom: 8px;
`;

const SectionHeader = styled.h3`
	margin-bottom: 8px;
	margin-top: 32px;

	:first-of-type {
		margin-top: 0;
	}

	color: ${colors.gray.g400};
	font-weight: normal;
`;

const ModuleSettingsContainer = styled.div`
	display: grid;
	margin-bottom: 16px;
	gap: 20px;
`;

const AddModuleSettingButton = styled(Button).attrs({
	variant: "outline-secondary",
	size: "sm",
})`
	width: fit-content;
	min-width: 20em;
`;

const GridContainer = styled.div`
	display: grid;
	grid-template-columns: auto auto;
	column-gap: 8px;
`;
